import React, { useEffect, useState } from 'react';
import { Button, Drawer, Form, Input, Select, Space, message, Modal } from 'antd';
import { passwordPolicyRule } from 'utils/passwordValidator';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { CountryCodeAndPhoneNumber, PhoneInput } from 'components/PhoneInput';
import { LIST_ALL_DEPTS } from '../../organization/graphql/queries/listAllDepts';
import { LIST_ALL_ROLES } from '../graphql/queries/listAllRoles';
import { FETCH_LARK_USER_ID } from '../graphql/queries/fetchLarkUserId';
import type { DeptFlatType } from '../../organization/graphql/queries/listAllDepts';
import type { RoleFlatType } from '../graphql/queries/listAllRoles';
import {
  ADD_OPDS_ACCOUNT,
  DELETE_OPDS_ACCOUNT,
  UPDATE_OPDS_ACCOUNT,
  type OpdsAccountApiRequestInput,
} from '../graphql/mutations/opdsAccountMutations';
import type { OpdsAccountType } from '../graphql/queries/queryOpdsAccountByPage';
import { getPlatformPermissionErrorMessage } from '../../utils/errorCode';
import { HasResource } from '../../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

type DrawerMode = 'create' | 'edit';

export interface PersonnelAccountDrawerProps {
  visible: boolean;
  mode: DrawerMode;
  onClose: () => void;
  onSuccess: () => void;
  /** 详情（编辑时由父组件在拉取详情后传入） */
  detail: OpdsAccountType | null;
}

const STATUS_FORM_OPTIONS = [
  { value: 1, labelKey: 'active' as const },
  { value: 0, labelKey: 'inactive' as const },
];

/** 多选下拉的 filterSort：把已选中的选项排到前面，未选中的保持原有相对顺序 */
const sortSelectedFirst = (selectedValues: number[] | undefined) =>
  (a: { value: number }, b: { value: number }) => {
    const aSelected = selectedValues?.includes(a.value) ? 0 : 1;
    const bSelected = selectedValues?.includes(b.value) ? 0 : 1;
    return aSelected - bSelected;
  };

function parsePhoneToLong(phone: CountryCodeAndPhoneNumber): number {
  const digits = (phone.phoneNumber || '').replace(/\D/g, '');
  if (!digits) return 0;
  const n = Number(digits);
  return Number.isFinite(n) ? n : 0;
}

const PersonnelAccountDrawer: React.FC<PersonnelAccountDrawerProps> = ({
  visible,
  mode,
  onClose,
  onSuccess,
  detail,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);

  const { data: deptData, loading: deptLoading } = useQuery(LIST_ALL_DEPTS, {
    skip: !visible,
    fetchPolicy: 'network-only',
  });

  const { data: roleData, loading: roleLoading } = useQuery(LIST_ALL_ROLES, {
    skip: !visible,
    fetchPolicy: 'network-only',
  });

  const [addAccount, { loading: adding }] = useMutation(ADD_OPDS_ACCOUNT);
  const [updateAccount, { loading: updating }] = useMutation(UPDATE_OPDS_ACCOUNT);
  const [deleteAccount, { loading: deleting }] = useMutation(DELETE_OPDS_ACCOUNT);
  const [fetchLarkUserId, { loading: fetchingLarkId }] = useLazyQuery(FETCH_LARK_USER_ID, {
    fetchPolicy: 'network-only',
  });

  const deptOptions =
    deptData?.listAllDepts?.map((d: DeptFlatType) => ({
      value: Number(d.id),
      label: `${d.fullName || d.name} (${d.id})`,
    })) ?? [];

  const roleOptions =
    roleData?.listAllRoles?.map((r: RoleFlatType) => ({
      value: Number(r.id),
      label: `${r.name} (${r.id})`,
    })) ?? [];

  const statusOptions = STATUS_FORM_OPTIONS.map((o) => ({
    value: o.value,
    label:
      o.labelKey === 'active'
        ? (t('central.platformPermission.personnel.filters.statusActive') as string)
        : (t('central.platformPermission.personnel.filters.statusInactive') as string),
  }));

  // 已选中的部门/角色排到下拉列表最前面，方便再次打开时快速找到
  const deptIdsValue = Form.useWatch('deptIds', form) as number[] | undefined;
  const roleIdsValue = Form.useWatch('roleIds', form) as number[] | undefined;

  // 下拉框每次关闭后换 key 强制重新挂载，保证下次打开时选项列表回到顶部（而不是停留在上次滚动位置）
  const [deptSelectKey, setDeptSelectKey] = useState(0);
  const [roleSelectKey, setRoleSelectKey] = useState(0);

  useEffect(() => {
    if (!visible) return;
    if (mode === 'create') {
      form.resetFields();
      form.setFieldsValue({
        phone: { countryCode: 1, phoneNumber: '' },
        status: 1,
        deptIds: [],
        roleIds: [],
        larkId: '',
      });
      return;
    }

    if (mode === 'edit' && detail) {
      // 先清空，避免部门/角色列表加载完成前，表单短暂展示上一个账号（或上次手动"获取"过）的残留数据
      form.resetFields();
      // 再加载部门/角色列表，避免详情先返回导致 Select 无法正确选中
      if (deptLoading || roleLoading) return;
      form.setFieldsValue({
        id: detail.id,
        phone: {
          countryCode: detail.countryCode ?? 1,
          phoneNumber: detail.phone != null ? String(detail.phone) : '',
        },
        password: '',
        name: detail.name ?? '',
        email: detail.email ?? '',
        status: detail.status ?? 1,
        deptIds: (detail.deptIds ?? []).map((x) => Number(x)),
        roleIds: (detail.roleIds ?? []).map((x) => Number(x)),
        larkId: detail.larkId ?? '',
      });
    }
  }, [visible, mode, detail, deptLoading, roleLoading, form]);

  const buildInput = (values: any): OpdsAccountApiRequestInput => {
    const phoneVal = values.phone as CountryCodeAndPhoneNumber;
    const phoneLong = parsePhoneToLong(phoneVal);
    const pwd = values.password != null ? String(values.password).trim() : '';
    const statusVal = values.status != null ? Number(values.status) : null;
    const base: OpdsAccountApiRequestInput = {
      id: mode === 'edit' && detail ? Number(detail.id) : undefined,
      email: values.email ? String(values.email).trim() : null,
      countryCode: phoneVal?.countryCode ?? 1,
      phone: phoneLong > 0 ? phoneLong : undefined,
      name: values.name ? String(values.name).trim() : null,
      status: Number.isFinite(statusVal as number) ? (statusVal as number) : null,
      deptIds: (values.deptIds as number[]) ?? [],
      roleIds: (values.roleIds as number[]) ?? [],
      // 未填写传空字符串，不传 undefined/null（与阶段二 OPDS 落库的空值语义保持一致）
      larkId: values.larkId ? String(values.larkId).trim() : '',
    };
    if (mode === 'create') {
      base.password = pwd;
    } else if (pwd.length > 0) {
      base.password = pwd;
    }
    return base;
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const input = buildInput(values);

      if (mode === 'create') {
        const res = await addAccount({ variables: { input } });
        const result = res.data?.addOpdsAccount;
        if (result?.success) {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.success(toastText);
          onSuccess();
          onClose();
        } else {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.error(toastText);
        }
      } else {
        const res = await updateAccount({ variables: { input } });
        const result = res.data?.updateOpdsAccount;
        if (result?.success) {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.success(toastText);
          onSuccess();
          onClose();
        } else {
          const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
          if (toastText) message.error(toastText);
        }
      }
    } catch (e: any) {
      if (Array.isArray(e?.errorFields)) return;
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    }
  };

  const handleFetchLarkId = async () => {
    const phoneVal = form.getFieldValue('phone') as CountryCodeAndPhoneNumber | undefined;
    const phoneLong = phoneVal ? parsePhoneToLong(phoneVal) : 0;
    const emailVal = (form.getFieldValue('email') as string | undefined)?.trim();
    if (!phoneLong && !emailVal) {
      message.error(t('central.platformPermission.personnel.messages.pleaseInputPhoneNumberBeforeFetchLarkId') as string);
      return;
    }
    try {
      const res = await fetchLarkUserId({
        variables: {
          input: {
            // phoneLong 为 0 代表未填写手机号，此时必须传 null 而不是 0——0 会被后端当成合法手机号 "0"
            phone: phoneLong || null,
            countryCode: phoneLong ? phoneVal?.countryCode ?? 1 : null,
            email: emailVal || null,
          },
        },
      });
      const result = res.data?.fetchLarkUserIdByContact;
      if (result?.success && result.larkId) {
        form.setFieldValue('larkId', result.larkId);
        message.success(t('central.platformPermission.personnel.messages.fetchLarkIdSuccess') as string);
      } else {
        const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message)
          || (t('central.platformPermission.personnel.messages.fetchLarkIdFailed') as string);
        message.error(toastText);
      }
    } catch (e: any) {
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message)
        || (t('central.platformPermission.personnel.messages.fetchLarkIdFailed') as string);
      message.error(toastText);
    }
  };

  const handleDelete = () => {
    if (!detail?.id) return;
    setDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!detail?.id) return;
    try {
      const res = await deleteAccount({
        variables: { input: { id: Number(detail.id) } },
      });
      const result = res.data?.deleteOpdsAccount;
      if (result?.success) {
        const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
        if (toastText) message.success(toastText);
        setDeleteModalOpen(false);
        onSuccess();
        onClose();
      } else {
        const toastText = getPlatformPermissionErrorMessage(t, result?.messageCode, result?.message);
        if (toastText) message.error(toastText);
      }
    } catch (e: any) {
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    }
  };

  return (
    <Drawer
      title={
        mode === 'create'
          ? t('central.platformPermission.personnel.drawer.add')
          : t('central.platformPermission.personnel.drawer.edit')
      }
      open={visible}
      onClose={onClose}
      width={650}
      footer={
        <div className="flex justify-between gap-2">
          <div>
            {mode === 'edit' && (
                <HasResource resourceCodes={[ResourceCode.personnel.DELETE]}>
                  <Button
                      loading={deleting}
                      onClick={handleDelete}
                      className="bg-red-500 border-red-500 text-white hover:!bg-red-500 hover:!border-red-500 hover:!text-white"
                  >
                    {t('components.button.delete')}
                  </Button>
                </HasResource>
            )}
          </div>
          <div className="flex gap-2">
            <HasResource resourceCodes={[ResourceCode.personnel.ADD, ResourceCode.personnel.UPDATE]}>
              <Button
                  type="primary"
                  loading={adding || updating}
                  onClick={handleSubmit}
              >
                {t('components.button.confirm') as string}
              </Button>
            </HasResource>
            <Button onClick={onClose}>
              {t('components.button.cancel') as string}
            </Button>
          </div>
        </div>
      }
    >
      <Form form={form} labelCol={{ span: 6 }} wrapperCol={{ span: 18 }} labelAlign="left" colon autoComplete="off">
        {mode === 'edit' && (
          <Form.Item name="id" label="ID">
            <Input disabled />
          </Form.Item>
        )}

        <Form.Item
          label={t('central.personnel.pageDetails.phone')}
          name="phone"
          rules={[
            { required: true, message: t('central.personnel.messages.pleaseInputPhoneNumber') as string },
            {
              validator: (_, value: CountryCodeAndPhoneNumber) => {
                if (!value || !value.phoneNumber) {
                  return Promise.reject(new Error(t('central.personnel.messages.phoneNumberRequired') as string));
                }
                if (value.countryCode === 1) {
                  if (value.phoneNumber.length !== 10) {
                    return Promise.reject(new Error(t('delivery.personnel.message.invalidPhoneNumberLength') as string));
                  }
                } else if (value.countryCode === 86) {
                  if (value.phoneNumber.length !== 11) {
                    return Promise.reject(new Error(t('delivery.personnel.message.invalidPhoneNumberLength') as string));
                  }
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <PhoneInput />
        </Form.Item>

        <Form.Item
          label={t('central.personnel.pageDetails.password')}
          name="password"
          validateTrigger="onBlur"
          rules={mode === 'create'
            ? [{ required: true, message: t('central.personnel.messages.pleaseInputPassword') as string }, passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]
            : [passwordPolicyRule(t('accountSecurity.passwordNotMeet') as string)]}
        >
          <Input.Password placeholder={t('accountSecurity.passwordHint') as string} autoComplete="new-password" />
        </Form.Item>

        <Form.Item
          label={t('central.personnel.pageDetails.name')}
          name="name"
          rules={[{ required: true, message: t('central.personnel.messages.pleaseInputName') as string }]}
        >
          <Input placeholder={t('components.input.typeIn') as string} />
        </Form.Item>

        <Form.Item
          label={t('central.personnel.pageDetails.email')}
          name="email"
          rules={[{ type: 'email', message: t('central.personnel.messages.pleaseInputValidEmail') as string }]}
        >
          <Input placeholder={t('components.input.typeIn') as string} />
        </Form.Item>

        <Form.Item
          label={t('central.platformPermission.personnel.fields.larkId') as string}
          name="larkId"
          rules={[{ max: 64, message: t('central.platformPermission.personnel.messages.larkIdMustNotExceed64Characters') as string }]}
        >
          <Space.Compact style={{ display: 'flex', width: '100%' }}>
            <Form.Item shouldUpdate noStyle style={{ flex: 1 }}>
              {() => (
                <Input
                  value={form.getFieldValue('larkId')}
                  onChange={(e) => form.setFieldValue('larkId', e.target.value)}
                  placeholder={t('components.input.typeIn') as string}
                  style={{ flex: 1 }}
                />
              )}
            </Form.Item>
            <Button
              type="primary"
              loading={fetchingLarkId}
              onClick={handleFetchLarkId}
              style={{ flexShrink: 0 }}
            >
              {t('central.platformPermission.personnel.actions.fetchLarkId') as string}
            </Button>
          </Space.Compact>
        </Form.Item>

        <Form.Item label={t('central.personnel.pageDetails.status')} name="status">
          <Select options={statusOptions} placeholder={t('components.input.select') as string} />
        </Form.Item>

        <Form.Item
          label={t('central.platformPermission.personnel.fields.deptIds')}
          name="deptIds"
          required
          rules={[
            {
              validator: (_, value: number[] | undefined) => {
                if (!value || value.length === 0) {
                  return Promise.reject(new Error(t('central.platformPermission.personnel.messages.deptRequired') as string));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Select
            key={deptSelectKey}
            mode="multiple"
            allowClear
            showSearch
            loading={deptLoading}
            optionFilterProp="label"
            autoClearSearchValue={false}
            filterSort={sortSelectedFirst(deptIdsValue)}
            maxTagCount="responsive"
            maxTagTextLength={(deptIdsValue?.length ?? 0) > 1 ? 25 : undefined}
            onOpenChange={(open) => { if (!open) setDeptSelectKey((k) => k + 1); }}
            placeholder={t('components.input.select') as string}
            options={deptOptions}
          />
        </Form.Item>

        <Form.Item
          label={t('central.platformPermission.personnel.fields.roleIds')}
          name="roleIds"
          required
          rules={[
            {
              validator: (_, value: number[] | undefined) => {
                if (!value || value.length === 0) {
                  return Promise.reject(new Error(t('central.platformPermission.personnel.messages.roleRequired') as string));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Select
            key={roleSelectKey}
            mode="multiple"
            allowClear
            showSearch
            loading={roleLoading}
            optionFilterProp="label"
            autoClearSearchValue={false}
            filterSort={sortSelectedFirst(roleIdsValue)}
            maxTagCount="responsive"
            onOpenChange={(open) => { if (!open) setRoleSelectKey((k) => k + 1); }}
            placeholder={t('components.input.select') as string}
            options={roleOptions}
          />
        </Form.Item>
      </Form>
      <Modal
        title={t('central.platformPermission.personnel.deleteConfirmTitle') as string}
        open={deleteModalOpen}
        onOk={handleConfirmDelete}
        onCancel={() => setDeleteModalOpen(false)}
        okText={t('components.button.confirm') as string}
        cancelText={t('components.button.cancel') as string}
        okButtonProps={{
          loading: deleting,
          className: 'bg-purple-600 border-purple-600 hover:!bg-purple-700 hover:!border-purple-700',
        }}
      >
        <p>{t('central.platformPermission.personnel.deleteConfirmContent') as string}</p>
      </Modal>
    </Drawer>
  );
};

export default PersonnelAccountDrawer;
